import { t } from "@/i18n";
import {
  DocumentStatusesEnum,
  ProcessUserStatusesEnum,
} from "@/store/modules/document/state";
import { StatusesEnum } from "@/store";
import { DocumentAccessTypes } from "@/types/documents/document/DocumentAccess";
import {
  EApprovalActionType,
  EApprovalStatus,
  EEntityType,
} from "@/types/common/entityApproval/EntityApproval";

export const getCommonStatusOptions = () => ({
  [StatusesEnum.ACTIVE]: t("status.active"),
  [StatusesEnum.DISABLED]: t("status.disabled"),
});

export const getCompanyTypeOptions = () => ({
  LEGAL: t("companyDetails.personTypeLegal"),
  INDIVIDUAL: t("companyDetails.personTypeIndividual"),
});

export const getCommonStatusLabel = (status?: string | null) => {
  const options = {
    [StatusesEnum.ACTIVE]: t("status.active"),
    [StatusesEnum.DISABLE]: t("status.disabled"),
    [StatusesEnum.DISABLED]: t("status.disabled"),
  };

  return status ? options[status as keyof typeof options] ?? status : "";
};

export const getDocumentStatusLabel = (status?: string | null) => {
  const options = {
    [DocumentStatusesEnum.NEW]: t("status.document.new"),
    [DocumentStatusesEnum.PROCESS]: t("status.document.process"),
    [DocumentStatusesEnum.REWORK]: t("status.document.rework"),
    [DocumentStatusesEnum.SUCCESS]: t("status.document.success"),
    [DocumentStatusesEnum.DENIED]: t("status.document.denied"),
    [DocumentStatusesEnum.DELETED]: t("status.document.deleted"),
  };

  return status ? options[status as keyof typeof options] ?? status : "";
};

export const getDocumentStatusFilterOptions = () => ({
  [DocumentStatusesEnum.NEW]: t("status.document.new"),
  [DocumentStatusesEnum.PROCESS]: t("status.document.process"),
  [DocumentStatusesEnum.SUCCESS]: t("status.document.success"),
  [DocumentStatusesEnum.DENIED]: t("status.document.denied"),
});

export const getProcessUserStatusLabel = (status?: string | null) => {
  const options = {
    [ProcessUserStatusesEnum.ACTIVE]: t("status.process.active"),
    [ProcessUserStatusesEnum.WAIT]: t("status.process.wait"),
    [ProcessUserStatusesEnum.SUCCESS]: t("status.process.success"),
    [ProcessUserStatusesEnum.DENIED]: t("status.process.denied"),
    [ProcessUserStatusesEnum.CANCELLED]: t("status.process.cancelled"),
  };

  return status ? options[status as keyof typeof options] ?? status : "";
};

export const getDocumentAccessLabel = (type?: string | null) => {
  const options = {
    [DocumentAccessTypes.VIEW]: t("documentAccess.view"),
    [DocumentAccessTypes.CREATOR]: t("documentAccess.creator"),
    [DocumentAccessTypes.CO_EDITOR]: t("documentAccess.coEditor"),
  };

  return type ? options[type as keyof typeof options] ?? type : "";
};

export const getPermissionLabel = (permission?: string | null) => {
  const options: Record<string, string> = {
    ENTITY_APPROVAL_COORDINATE: t("permission.entityApprovalCoordinate"),
    DOCUMENT_CONSTRUCTOR_CREATE: t("permission.documentConstructorCreate"),
    DOCUMENT_CONSTRUCTOR_EDIT: t("permission.documentConstructorEdit"),
    DOCUMENT_CONSTRUCTOR_DEACTIVATE: t(
      "permission.documentConstructorDeactivate"
    ),
    DOCUMENT_TEMPLATE_CREATE: t("permission.documentTemplateCreate"),
    DOCUMENT_TEMPLATE_EDIT: t("permission.documentTemplateEdit"),
    DOCUMENT_TEMPLATE_DEACTIVATE: t("permission.documentTemplateDeactivate"),
    DOCUMENT_BATCH_CREATE: t("permission.documentBatchCreate"),
    COMPANY_CREATE: t("permission.companyCreate"),
    COMPANY_EDIT: t("permission.companyEdit"),
    COMPANY_DEACTIVATE: t("permission.companyDeactivate"),
    USER_CREATE: t("permission.userCreate"),
    USER_EDIT: t("permission.userEdit"),
    USER_DEACTIVATE: t("permission.userDeactivate"),
    USER_VIEW_TENANT: t("permission.userViewTenant"),
    USER_EDIT_TENANT: t("permission.userEditTenant"),
    REPORT_CREATE: t("permission.reportCreate"),
    REPORT_EDIT: t("permission.reportEdit"),
    DOCUMENT_ACCESS_CO_EDITOR_GRANT: t(
      "permission.documentAccessCoEditorGrant"
    ),
    HELPDESK_MANAGE: t("permission.helpdeskManage"),
    HR_VIEW: t("permission.hrView"),
    HR_EMPLOYEE_EDIT: t("permission.hrEmployeeEdit"),
    HR_DOCUMENT_MANAGE: t("permission.hrDocumentManage"),
    HR_SETTINGS_MANAGE: t("permission.hrSettingsManage"),
    UI_DARK_THEME: t("permission.uiDarkTheme"),
    HR_ASSISTANT_EMPLOYEE_SEARCH: t("permission.hrAssistantEmployeeSearch"),
    HR_ASSISTANT_EMPLOYEE_VIEW: t("permission.hrAssistantEmployeeView"),
    HR_ASSISTANT_DOCUMENT_SEARCH: t("permission.hrAssistantDocumentSearch"),
    HR_ASSISTANT_DOCUMENT_VIEW: t("permission.hrAssistantDocumentView"),
    HR_ASSISTANT_FILE_VIEW: t("permission.hrAssistantFileView"),
    ENBEK_MANAGE: t("permission.enbekManage"),
  };

  return permission ? options[permission] ?? permission : "";
};

export const getEntityApprovalTypeLabel = (type: EEntityType) => {
  const labels: Record<EEntityType, string> = {
    [EEntityType.DOCUMENT_CONSTRUCTOR]: t(
      "entityApproval.type.documentConstructor"
    ),
    [EEntityType.DOCUMENT_TEMPLATE]: t("entityApproval.type.documentTemplate"),
    [EEntityType.COMPANY]: t("entityApproval.type.company"),
    [EEntityType.USER]: t("entityApproval.type.user"),
  };
  return labels[type] ?? type;
};

export const getEntityApprovalActionLabel = (type: EApprovalActionType) => {
  const labels: Record<EApprovalActionType, string> = {
    [EApprovalActionType.CREATE]: t("entityApproval.action.create"),
    [EApprovalActionType.UPDATE]: t("entityApproval.action.update"),
    [EApprovalActionType.DEACTIVATE]: t("entityApproval.action.deactivate"),
  };
  return labels[type] ?? type;
};

export const getEntityApprovalStatusLabel = (status: EApprovalStatus) => {
  const labels: Record<EApprovalStatus, string> = {
    [EApprovalStatus.WAITING]: t("entityApproval.status.waiting"),
    [EApprovalStatus.APPROVED]: t("entityApproval.status.approved"),
    [EApprovalStatus.REJECTED]: t("entityApproval.status.rejected"),
  };
  return labels[status] ?? status;
};
